import { useEffect, useState } from "react";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import { getOrders } from "../../api/ordersApi";
import { CURRENCY_MAP } from "../../constants";
import { useNavigate } from "react-router-dom";

const OrdersPage = () => {
  const [limit, setLimit] = useState(10);

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const totalPages = Math.ceil(total / limit);

  const [toast, setToast] = useState(null);

  const getPageNumbers = () => {
    const delta = 2; // pages to show on each side of current page
    const range = [];

    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    range.push(1); // always show first page

    if (left > 2) range.push("...");

    for (let i = left; i <= right; i++) range.push(i);

    if (right < totalPages - 1) range.push("...");

    if (totalPages > 1) range.push(totalPages); // always show last page

    return range;
  };

  const formatCurrency = (amount, currency) => {
    const symbol = CURRENCY_MAP[currency] || currency;
    return `${symbol} ${parseFloat(amount).toFixed(2)}`;
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const offset = (page - 1) * limit;
      const data = await getOrders(limit, offset);

      setOrders(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setError("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (e) => {
    setPage(1);
    setLimit(Number(e.target.value));
  };

  useEffect(() => {
    fetchOrders();
  }, [page, limit]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Orders</h1>
          <p className="text-sm text-slate-400">
            Manage Orders information and details
          </p>
        </div>
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-center text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4">Id</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Event</th>
                <th className="px-6 py-4">Customer Details</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Total</th>
              </tr>
            </thead>

            <tbody>
              {orders.map((od) => (
                <tr
                  key={od.id}
                  onClick={() =>
                    navigate(`/dashboard/orders/${od.id}`)
                  }
                  className="border-b border-white/5 hover:bg-slate-800/40 transition cursor-pointer text-center"
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-300">
                      {od.id}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {od.status || "-"}
                  </td>

                   <td className="px-6 py-4 text-sm text-slate-300">
                    {od.event?.name || "-"}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {od.full_name} <br /> {od.email}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {od.type || "-"}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {formatCurrency(od.total, od.currency) || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state placeholder (future) */}
        {orders.length === 0 && (
          <div className="p-8 text-center text-slate-400">No orders found.</div>
        )}
      </div>

      <div className="flex justify-end">
        <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
          <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
            {/* ── Rows per page — always visible ── */}
            <span className="text-slate-400">Rows per page:</span>
            <select
              value={limit}
              onChange={handleLimitChange}
              className="bg-slate-800 text-slate-300 border border-white/10 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-pink-500 cursor-pointer"
            >
              {[5, 10, 25, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            {totalPages > 1 && (
              <>
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber, index) =>
                    pageNumber === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-1 text-slate-500 select-none"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNumber}
                        onClick={() => setPage(pageNumber)}
                        className={`px-3 py-1 rounded-md border border-white/10 transition
          ${
            page === pageNumber
              ? "bg-pink-600 text-white"
              : "text-slate-300 hover:bg-slate-800 hover:text-white"
          }`}
                      >
                        {pageNumber}
                      </button>
                    ),
                  )}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default OrdersPage;
